import { createSlice, createAsyncThunk, createEntityAdapter, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { collection, doc, addDoc, updateDoc, deleteDoc, query, where, getDocs, serverTimestamp, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase'; // Assuming db is exported from your firebase config
import { RootState } from '@/store'; // Assuming RootState is defined

// --- Types ---
export interface CustomMapFirestoreData {
  name: string;
  ownerId: string;
  ownerUsername: string;
  isPublic: boolean;
  description?: string;
  locationIds: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ReduxCustomMap {
  id: string;
  name: string;
  ownerId: string;
  ownerUsername: string;
  isPublic: boolean;
  description?: string;
  locationIds: string[];
  createdAt: number; // Serialized timestamp
  updatedAt: number; // Serialized timestamp
}

interface CustomMapsState {
  myMaps: EntityState<ReduxCustomMap, string>;
  publicMaps: EntityState<ReduxCustomMap, string>;
  loadingMyMaps: 'idle' | 'pending' | 'succeeded' | 'failed';
  loadingPublicMaps: 'idle' | 'pending' | 'succeeded' | 'failed';
  myMapsError: string | null;
  publicMapsError: string | null;
  operationLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
  operationError: string | null;
  selectedMapId: string | null; 
  currentMapLocationIds: string[]; // For filtering locations on the main map
  myMapsListenerActive: boolean;
  publicMapsListenerActive: boolean;
}

// --- Entity Adapters ---
const myMapsAdapter = createEntityAdapter<ReduxCustomMap>();
const publicMapsAdapter = createEntityAdapter<ReduxCustomMap>();

// --- Initial State ---
const initialState: CustomMapsState = {
  myMaps: myMapsAdapter.getInitialState(),
  publicMaps: publicMapsAdapter.getInitialState(),
  loadingMyMaps: 'idle',
  loadingPublicMaps: 'idle',
  myMapsError: null,
  publicMapsError: null,
  operationLoading: 'idle',
  operationError: null,
  selectedMapId: null,
  currentMapLocationIds: [],
  myMapsListenerActive: false,
  publicMapsListenerActive: false
};

// Track active listeners to avoid duplicates
let myMapsUnsubscribe: (() => void) | null = null;
let publicMapsUnsubscribe: (() => void) | null = null;

// --- Helper to convert Firestore Timestamps to numbers ---
const convertMapToRedux = (id: string, data: CustomMapFirestoreData): ReduxCustomMap => ({
  id,
  ...data,
  createdAt: data.createdAt?.toMillis() || Date.now(),
  updatedAt: data.updatedAt?.toMillis() || Date.now(),
});

// --- Async Thunks ---

// Initialize real-time listener for current user's maps
export const initializeMyMapsListener = createAsyncThunk<
  void,
  string, // userId
  { // Corrected ThunkApiConfig
    state: RootState, // This ensures getState() returns RootState
    dispatch: any; // You can use a more specific type for dispatch if available (e.g., AppDispatch)
    // rejectValue: string; // Optional: add if this thunk uses rejectWithValue
  }
>('customMaps/initializeMyMapsListener', async (userId, { dispatch, getState }) => {
  const state = getState().customMaps;
  
  // Don't set up duplicate listeners
  if (state.myMapsListenerActive) {
    console.log('[CustomMapsSlice] My maps listener already active, skipping initialization');
    return;
  }
  
  console.log(`[CustomMapsSlice] Setting up my maps listener for user: ${userId}`);
  
  // Clean up any existing listener first
  if (myMapsUnsubscribe) {
    myMapsUnsubscribe();
    myMapsUnsubscribe = null;
  }
  
  const q = query(collection(db, 'customMaps'), where('ownerId', '==', userId));
  
  // Set up the real-time listener
  myMapsUnsubscribe = onSnapshot(
    q,
    (snapshot) => {
      // Handle document changes to provide real-time updates
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data() as CustomMapFirestoreData;
        const map = convertMapToRedux(change.doc.id, data);
        
        if (change.type === 'added' || change.type === 'modified') {
          // Add or update the map in state
          dispatch(updateMyMap(map));
        } else if (change.type === 'removed') {
          // Remove the map from state
          dispatch(removeMyMap(map.id));
        }
      });
      
      // Initial load marks the action as successful
      if (snapshot.docs.length > 0) {
        dispatch(setMyMapsListenerActive(true));
      }
    },
    (error) => {
      console.error("Error in my maps listener:", error);
      dispatch(setMyMapsError(error.message || 'Failed to listen to user maps'));
      // Cleanup on error
      if (myMapsUnsubscribe) {
        myMapsUnsubscribe();
        myMapsUnsubscribe = null;
      }
      dispatch(setMyMapsListenerActive(false));
    }
  );
});

// Initialize real-time listener for public maps
export const initializePublicMapsListener = createAsyncThunk<
  void,
  string | null, // userId (null if not authenticated)
  { // Corrected ThunkApiConfig
    state: RootState,
    dispatch: any
    // rejectValue: string; // Optional
  }
>('customMaps/initializePublicMapsListener', async (userId, { dispatch, getState }) => {
  const state = getState().customMaps;
  
  // Don't set up duplicate listeners
  if (state.publicMapsListenerActive) {
    console.log('[CustomMapsSlice] Public maps listener already active, skipping initialization');
    return;
  }
  
  // REMOVED: Authentication check - public maps should be accessible to all users
  console.log('[CustomMapsSlice] Setting up public maps listener');
  
  // Clean up any existing listener first
  if (publicMapsUnsubscribe) {
    publicMapsUnsubscribe();
    publicMapsUnsubscribe = null;
  }
  
  const q = query(collection(db, 'customMaps'), where('isPublic', '==', true));
  
  // Set up the real-time listener
  publicMapsUnsubscribe = onSnapshot(
    q,
    (snapshot) => {
      // Handle document changes to provide real-time updates
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data() as CustomMapFirestoreData;
        const map = convertMapToRedux(change.doc.id, data);
        
        if (change.type === 'added' || change.type === 'modified') {
          // Add or update the map in state
          dispatch(updatePublicMap(map));
        } else if (change.type === 'removed') {
          // Remove the map from state
          dispatch(removePublicMap(map.id));
        }
      });
      
      // Initial load marks the action as successful
      dispatch(setPublicMapsListenerActive(true));
      
      if (snapshot.docs.length === 0) {
        console.log('[CustomMapsSlice] Public maps listener active, no public maps found.');
      } else {
        console.log(`[CustomMapsSlice] Public maps listener active, ${snapshot.docs.length} maps loaded.`);
      }
    },
    (error) => {
      console.error("Error in public maps listener:", error);
      
      // Provide more specific error messages for debugging
      let errorMessage = error.message || 'Failed to listen to public maps';
      if (error.code === 'permission-denied' && !userId) {
        errorMessage = 'Permission denied: Check Firestore rules for public map access by unauthenticated users';
        console.warn('[CustomMapsSlice] Permission denied for public maps (unauthenticated). Check Firestore rules.');
      } else if (error.code === 'permission-denied') {
        console.warn(`[CustomMapsSlice] Permission denied for public maps (user: ${userId}). This should not happen.`);
      }

      dispatch(setPublicMapsError(errorMessage));
      // Cleanup on error
      if (publicMapsUnsubscribe) {
        publicMapsUnsubscribe();
        publicMapsUnsubscribe = null;
      }
      dispatch(setPublicMapsListenerActive(false));
    }
  );
});

// Clean up all listeners
export const cleanupMapListeners = createAsyncThunk<
  void,
  void,
  { dispatch: any }
>('customMaps/cleanupMapListeners', async (_, { dispatch }) => {
  console.log('[CustomMapsSlice] Cleaning up all map listeners');
  
  if (myMapsUnsubscribe) {
    myMapsUnsubscribe();
    myMapsUnsubscribe = null;
    dispatch(setMyMapsListenerActive(false));
  }
  
  if (publicMapsUnsubscribe) {
    publicMapsUnsubscribe();
    publicMapsUnsubscribe = null;
    dispatch(setPublicMapsListenerActive(false));
  }
});

// Keeping the old thunks for compatibility but they will be deprecated as we move to listeners

// Fetch current user's maps
export const fetchMyMaps = createAsyncThunk<
  ReduxCustomMap[],
  string, // userId
  { rejectValue: string }
>('customMaps/fetchMyMaps', async (userId, { rejectWithValue }) => {
  try {
    const q = query(collection(db, 'customMaps'), where('ownerId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertMapToRedux(doc.id, doc.data() as CustomMapFirestoreData));
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch user maps');
  }
});

// Fetch public maps (potentially with pagination/filtering later)
export const fetchPublicMaps = createAsyncThunk<
  ReduxCustomMap[],
  void, // No arguments for now
  { rejectValue: string }
>('customMaps/fetchPublicMaps', async (_, { rejectWithValue }) => {
  try {
    const q = query(collection(db, 'customMaps'), where('isPublic', '==', true));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertMapToRedux(doc.id, doc.data() as CustomMapFirestoreData));
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch public maps');
  }
});

// Create a new custom map
export const createCustomMap = createAsyncThunk<
  ReduxCustomMap,
  { name: string; description?: string; ownerId: string; ownerUsername: string },
  { rejectValue: string }
>('customMaps/createCustomMap', async (mapData, { rejectWithValue }) => {
  try {
    const newMapData: Omit<CustomMapFirestoreData, 'createdAt' | 'updatedAt'> & { createdAt?: Timestamp, updatedAt?: Timestamp } = {
      ...mapData,
      isPublic: false, // Defaults to private
      locationIds: [],
      createdAt: serverTimestamp() as Timestamp, // Firestore will set this
      updatedAt: serverTimestamp() as Timestamp, // Firestore will set this
    };
    const docRef = await addDoc(collection(db, 'customMaps'), newMapData);
    // Firestore doesn't return the doc with server timestamps immediately in the write result.
    // For consistency, we'll use client-generated timestamps for the Redux state temporarily,
    // or refetch. For simplicity now, let's simulate the server timestamps.
    return {
      id: docRef.id,
      name: mapData.name,
      ownerId: mapData.ownerId,
      ownerUsername: mapData.ownerUsername,
      isPublic: false,
      description: mapData.description,
      locationIds: [],
      createdAt: Date.now(), 
      updatedAt: Date.now(),
    };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create map');
  }
});

// Update a custom map (name, description, isPublic)
export const updateCustomMapDetails = createAsyncThunk<
  Partial<ReduxCustomMap> & { id: string }, // Returns the ID and updated fields
  { mapId: string; name?: string; description?: string; isPublic?: boolean },
  { rejectValue: string }
>('customMaps/updateCustomMapDetails', async ({ mapId, ...detailsToUpdate }, { rejectWithValue }) => {
  try {
    const mapRef = doc(db, 'customMaps', mapId);
    const updatePayload: Partial<CustomMapFirestoreData> = { ...detailsToUpdate, updatedAt: serverTimestamp() as Timestamp };
    await updateDoc(mapRef, updatePayload);
    return { 
      id: mapId, 
      ...detailsToUpdate, 
      updatedAt: Date.now() // Simulate for Redux state
    };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update map details');
  }
});

// Add a location to a custom map
export const addLocationToMap = createAsyncThunk<
  { mapId: string; locationId: string },
  { mapId: string; locationId: string },
  { rejectValue: string; state: RootState }
>('customMaps/addLocationToMap', async ({ mapId, locationId }, { rejectWithValue, getState }) => {
  try {
    const map = myMapsAdapter.getSelectors().selectById(getState().customMaps.myMaps, mapId);
    if (!map) throw new Error('Map not found');
    if (map.locationIds.includes(locationId)) return rejectWithValue('Location already in map');

    const mapRef = doc(db, 'customMaps', mapId);
    const newLocationIds = [...map.locationIds, locationId];
    await updateDoc(mapRef, { 
      locationIds: newLocationIds,
      updatedAt: serverTimestamp() as Timestamp 
    });
    return { mapId, locationId }; // Only need this to update Redux state
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to add location to map');
  }
});

// Remove a location from a custom map
export const removeLocationFromMap = createAsyncThunk<
  { mapId: string; locationId: string },
  { mapId: string; locationId: string },
  { rejectValue: string; state: RootState }
>('customMaps/removeLocationFromMap', async ({ mapId, locationId }, { rejectWithValue, getState }) => {
  try {
    const map = myMapsAdapter.getSelectors().selectById(getState().customMaps.myMaps, mapId);
    if (!map) throw new Error('Map not found');

    const mapRef = doc(db, 'customMaps', mapId);
    const newLocationIds = map.locationIds.filter(id => id !== locationId);
    await updateDoc(mapRef, { 
      locationIds: newLocationIds,
      updatedAt: serverTimestamp() as Timestamp 
    });
    return { mapId, locationId };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to remove location from map');
  }
});

// Delete a custom map
export const deleteCustomMap = createAsyncThunk<
  string, // mapId
  string, // mapId
  { rejectValue: string }
>('customMaps/deleteCustomMap', async (mapId, { rejectWithValue }) => {
  try {
    const mapRef = doc(db, 'customMaps', mapId);
    await deleteDoc(mapRef);
    return mapId;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete map');
  }
});

// --- Slice Definition ---
const customMapsSlice = createSlice({
  name: 'customMaps',
  initialState,
  reducers: {
    // Sync handler actions for real-time updates
    updateMyMap: (state, action: PayloadAction<ReduxCustomMap>) => {
      myMapsAdapter.upsertOne(state.myMaps, action.payload);
      // If this map is currently selected, update currentMapLocationIds
      if (state.selectedMapId === action.payload.id) {
        state.currentMapLocationIds = [...action.payload.locationIds];
      }
    },
    removeMyMap: (state, action: PayloadAction<string>) => {
      myMapsAdapter.removeOne(state.myMaps, action.payload);
      // If this was the selected map, clear the selection
      if (state.selectedMapId === action.payload) {
        state.selectedMapId = null;
        state.currentMapLocationIds = [];
      }
    },
    updatePublicMap: (state, action: PayloadAction<ReduxCustomMap>) => {
      publicMapsAdapter.upsertOne(state.publicMaps, action.payload);
      // If this map is currently selected, update currentMapLocationIds
      if (state.selectedMapId === action.payload.id) {
        state.currentMapLocationIds = [...action.payload.locationIds];
      }
    },
    removePublicMap: (state, action: PayloadAction<string>) => {
      publicMapsAdapter.removeOne(state.publicMaps, action.payload);
      // If this was the selected map, clear the selection
      if (state.selectedMapId === action.payload) {
        state.selectedMapId = null;
        state.currentMapLocationIds = [];
      }
    },
    setMyMapsListenerActive: (state, action: PayloadAction<boolean>) => {
      state.myMapsListenerActive = action.payload;
      // When the listener becomes active, set loading to succeeded
      if (action.payload) {
        state.loadingMyMaps = 'succeeded';
      }
    },
    setPublicMapsListenerActive: (state, action: PayloadAction<boolean>) => {
      state.publicMapsListenerActive = action.payload;
      // When the listener becomes active, set loading to succeeded
      if (action.payload) {
        state.loadingPublicMaps = 'succeeded';
      }
    },
    setMyMapsError: (state, action: PayloadAction<string>) => {
      state.myMapsError = action.payload;
      state.loadingMyMaps = 'failed';
    },
    setPublicMapsError: (state, action: PayloadAction<string>) => {
      state.publicMapsError = action.payload;
      state.loadingPublicMaps = 'failed';
    },
    
    // Existing reducers
    setSelectedMapId: (state, action: PayloadAction<string | null>) => {
      state.selectedMapId = action.payload;
      if (action.payload) {
        // First check in myMaps
        let selectedMap = myMapsAdapter.getSelectors().selectById(state.myMaps, action.payload);
        
        // If not found in myMaps, check in publicMaps
        if (!selectedMap) {
          selectedMap = publicMapsAdapter.getSelectors().selectById(state.publicMaps, action.payload);
        }
        
        if (selectedMap) {
          console.log(`[CustomMapsSlice] Found map ${action.payload} with ${selectedMap.locationIds.length} locations`);
          state.currentMapLocationIds = [...selectedMap.locationIds]; // Create a new array reference
        } else {
          console.log(`[CustomMapsSlice] Warning: Map ${action.payload} not found in either myMaps or publicMaps`);
          state.currentMapLocationIds = [];
        }
      } else {
        console.log('[CustomMapsSlice] Clearing selected map');
        state.currentMapLocationIds = [];
      }
    },
    clearSelectedMap: (state) => {
      state.selectedMapId = null;
      state.currentMapLocationIds = [];
    },
    resetCustomMapOperationStatus: (state) => {
      state.operationLoading = 'idle';
      state.operationError = null;
    },
    clearMyMapsData: (state) => {
      state.myMaps = myMapsAdapter.getInitialState();
      state.loadingMyMaps = 'idle';
      state.myMapsError = null;
      state.selectedMapId = null; 
      state.currentMapLocationIds = [];
    }
  },
  extraReducers: (builder) => {
    // fetchMyMaps
    builder.addCase(fetchMyMaps.pending, (state) => {
      state.loadingMyMaps = 'pending';
      state.myMapsError = null;
    });
    builder.addCase(fetchMyMaps.fulfilled, (state, action) => {
      myMapsAdapter.setAll(state.myMaps, action.payload);
      state.loadingMyMaps = 'succeeded';
    });
    builder.addCase(fetchMyMaps.rejected, (state, action) => {
      state.loadingMyMaps = 'failed';
      state.myMapsError = action.payload ?? 'Unknown error';
    });

    // fetchPublicMaps
    builder.addCase(fetchPublicMaps.pending, (state) => {
      state.loadingPublicMaps = 'pending';
      state.publicMapsError = null;
    });
    builder.addCase(fetchPublicMaps.fulfilled, (state, action) => {
      publicMapsAdapter.setAll(state.publicMaps, action.payload);
      state.loadingPublicMaps = 'succeeded';
    });
    builder.addCase(fetchPublicMaps.rejected, (state, action) => {
      state.loadingPublicMaps = 'failed';
      state.publicMapsError = action.payload ?? 'Unknown error';
    });

    // createCustomMap
    builder.addCase(createCustomMap.pending, (state) => {
      state.operationLoading = 'pending';
      state.operationError = null;
    });
    builder.addCase(createCustomMap.fulfilled, (state, action) => {
      myMapsAdapter.addOne(state.myMaps, action.payload); // Add to user's maps
      state.operationLoading = 'succeeded';
    });
    builder.addCase(createCustomMap.rejected, (state, action) => {
      state.operationLoading = 'failed';
      state.operationError = action.payload ?? 'Unknown error';
    });

    // updateCustomMapDetails
    builder.addCase(updateCustomMapDetails.pending, (state) => {
      state.operationLoading = 'pending';
      state.operationError = null;
    });
    builder.addCase(updateCustomMapDetails.fulfilled, (state, action) => {
      myMapsAdapter.updateOne(state.myMaps, {id: action.payload.id, changes: action.payload });
      // If it was public and is now in publicMaps, update it there too
      if (publicMapsAdapter.getSelectors().selectById(state.publicMaps, action.payload.id)) {
          if (action.payload.isPublic === false) { // Became private
            publicMapsAdapter.removeOne(state.publicMaps, action.payload.id);
          } else { // Still public or became public, update details
            publicMapsAdapter.updateOne(state.publicMaps, {id: action.payload.id, changes: action.payload });
          }
      } else if (action.payload.isPublic === true) { // Was not in public, but now is
         const mapFromMyMaps = myMapsAdapter.getSelectors().selectById(state.myMaps, action.payload.id);
         if(mapFromMyMaps) publicMapsAdapter.addOne(state.publicMaps, mapFromMyMaps); // Add if found
      }
      state.operationLoading = 'succeeded';
    });
    builder.addCase(updateCustomMapDetails.rejected, (state, action) => {
      state.operationLoading = 'failed';
      state.operationError = action.payload ?? 'Unknown error';
    });

    // addLocationToMap
    builder.addCase(addLocationToMap.pending, (state) => {
      state.operationLoading = 'pending';
      state.operationError = null;
    });
    builder.addCase(addLocationToMap.fulfilled, (state, action) => {
      const { mapId, locationId } = action.payload;
      const map = myMapsAdapter.getSelectors().selectById(state.myMaps, mapId);
      if (map && !map.locationIds.includes(locationId)) {
        myMapsAdapter.updateOne(state.myMaps, {
          id: mapId,
          changes: { locationIds: [...map.locationIds, locationId], updatedAt: Date.now() },
        });
         // Update currentMapLocationIds if this map is selected
        if (state.selectedMapId === mapId) {
            state.currentMapLocationIds = [...map.locationIds, locationId];
        }
      }
      state.operationLoading = 'succeeded';
    });
    builder.addCase(addLocationToMap.rejected, (state, action) => {
      state.operationLoading = 'failed';
      state.operationError = action.payload ?? 'Unknown error';
    });

    // removeLocationFromMap
    builder.addCase(removeLocationFromMap.pending, (state) => {
      state.operationLoading = 'pending';
      state.operationError = null;
    });
    builder.addCase(removeLocationFromMap.fulfilled, (state, action) => {
      const { mapId, locationId } = action.payload;
      const map = myMapsAdapter.getSelectors().selectById(state.myMaps, mapId);
      if (map) {
        const newLocationIds = map.locationIds.filter(id => id !== locationId);
        myMapsAdapter.updateOne(state.myMaps, {
          id: mapId,
          changes: { locationIds: newLocationIds, updatedAt: Date.now() },
        });
        // Update currentMapLocationIds if this map is selected
        if (state.selectedMapId === mapId) {
            state.currentMapLocationIds = newLocationIds;
        }
      }
      state.operationLoading = 'succeeded';
    });
    builder.addCase(removeLocationFromMap.rejected, (state, action) => {
      state.operationLoading = 'failed';
      state.operationError = action.payload ?? 'Unknown error';
    });
    
    // deleteCustomMap
    builder.addCase(deleteCustomMap.pending, (state) => {
      state.operationLoading = 'pending';
      state.operationError = null;
    });
    builder.addCase(deleteCustomMap.fulfilled, (state, action) => {
      myMapsAdapter.removeOne(state.myMaps, action.payload);
      publicMapsAdapter.removeOne(state.publicMaps, action.payload); // Also remove if it was public
      if (state.selectedMapId === action.payload) {
        state.selectedMapId = null;
        state.currentMapLocationIds = [];
      }
      state.operationLoading = 'succeeded';
    });
    builder.addCase(deleteCustomMap.rejected, (state, action) => {
      state.operationLoading = 'failed';
      state.operationError = action.payload ?? 'Unknown error';
    });
  },
});

// --- Selectors ---
export const { selectAll: selectAllMyMaps, selectById: selectMyMapById } =
  myMapsAdapter.getSelectors((state: RootState) => state.customMaps.myMaps);

export const { selectAll: selectAllPublicMaps, selectById: selectPublicMapById } =
  publicMapsAdapter.getSelectors((state: RootState) => state.customMaps.publicMaps);

export const selectMyMapsLoading = (state: RootState) => state.customMaps.loadingMyMaps;
export const selectPublicMapsLoading = (state: RootState) => state.customMaps.loadingPublicMaps;
export const selectMyMapsError = (state: RootState) => state.customMaps.myMapsError;
export const selectPublicMapsError = (state: RootState) => state.customMaps.publicMapsError;
export const selectCustomMapOperationLoading = (state: RootState) => state.customMaps.operationLoading;
export const selectCustomMapOperationError = (state: RootState) => state.customMaps.operationError;
export const selectSelectedMapId = (state: RootState) => state.customMaps.selectedMapId;
export const selectCurrentMapLocationIds = (state: RootState) => state.customMaps.currentMapLocationIds;
export const selectMyMapsListenerActive = (state: RootState) => state.customMaps.myMapsListenerActive;
export const selectPublicMapsListenerActive = (state: RootState) => state.customMaps.publicMapsListenerActive;

export const {
  setSelectedMapId,
  clearSelectedMap,
  resetCustomMapOperationStatus,
  clearMyMapsData,
  updateMyMap,
  removeMyMap,
  updatePublicMap,
  removePublicMap,
  setMyMapsListenerActive,
  setPublicMapsListenerActive,
  setMyMapsError,
  setPublicMapsError
} = customMapsSlice.actions;

export default customMapsSlice.reducer; 